export const PHASES = ["foresporsel", "tilbud", "bestilt", "klargjoring", "produksjon", "montering", "levert"];

export const PHASE_LABELS = {
  foresporsel: "Forespørsel",
  tilbud: "Tilbud",
  bestilt: "Bestilt",
  klargjoring: "Klargjøring",
  produksjon: "Produksjon",
  montering: "Montering",
  levert: "Levert",
};

export const CHANGE_STATUS_LABELS = {
  registrert: "Registrert – venter på vurdering",
  vurdert: "Konsekvens vurdert",
  grunnlag_oppdatert: "Nytt grunnlag klart",
  til_kundegodkjenning: "Venter på kundegodkjenning",
  kundegodkjent: "Kundegodkjent – klar til intern kontroll",
  endring_onsket: "Kunden ber om endring",
  frigitt: "Frigitt",
  avvist: "Avsluttet uten gjennomføring",
};

export const SPEC_STATUS_LABELS = {
  utkast: "Utkast",
  til_kundegodkjenning: "Til kundegodkjenning",
  kundegodkjent: "Kundegodkjent – ikke frigitt",
  frigitt: "Frigitt – gjeldende",
  erstattet: "Erstattet",
  avvist: "Avvist",
};

export const DRAWING_STATUS_LABELS = {
  utkast: "Utkast",
  til_godkjenning: "Til kundegodkjenning",
  kundegodkjent: "Kundegodkjent – ikke frigitt",
  gjeldende: "Gjeldende",
  erstattet: "Erstattet",
  avvist: "Avvist",
};

export const SERVICE_STATUS_LABELS = { apen: "Åpen", planlagt: "Planlagt", utfort: "Utført", lukket: "Lukket" };
export const SERVICE_TYPE_LABELS = { mangel: "Mangel", skade: "Skade", oppfolging: "Oppfølging", reklamasjon: "Reklamasjon", service: "Service", restpunkt: "Restpunkt" };
export const BILLABLE_LABELS = { ikke_fakturerbar: "Ikke fakturerbar", fakturerbar: "Fakturerbar", under_vurdering: "Under vurdering" };
export const INSTALL_STATUS_LABELS = {
  ikke_planlagt: "Ikke planlagt",
  planlagt: "Planlagt",
  pagar: "Pågår",
  montert: "Montert",
  montert_med_restpunkter: "Montert med restpunkter",
};
export const PRODUCTION_STATUS_LABELS = { ikke_planlagt: "Ikke planlagt", planlagt: "Planlagt", startet: "Startet", ferdig: "Ferdig produsert" };
export const TASK_STATUS_LABELS = { fullfort: "Utført", planlagt: "Planlagt", mangler: "Mangler", ikke_aktuelt: "Ikke aktuelt" };

export function weekLabel(week) {
  if (!week) return "–";
  const [, w] = week.split("-W");
  return `Uke ${parseInt(w, 10)}`;
}

/** ISO week id (YYYY-Www) for a date. */
export function isoWeekOf(date) {
  const tmp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
  return `${tmp.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

/** Consecutive ISO weeks starting `startOffset` weeks from today. */
export function weekRange(startOffset = 0, count = 12) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < count; i++) out.push(isoWeekOf(new Date(now.getTime() + (startOffset + i) * 7 * 86400000)));
  return Array.from(new Set(out));
}

export function weekYear(week) {
  if (!week) return "";
  return week.split("-W")[0];
}

export function formatDate(iso, opts = {}) {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("nb-NO", { day: "numeric", month: "short", ...(opts.year ? { year: "numeric" } : {}) });
}

export function formatDateTime(iso) {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("nb-NO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function formatDateLong(iso) {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("nb-NO", { weekday: "long", day: "numeric", month: "long" });
}

export function formatAmount(n) {
  if (n === null || n === undefined) return "Ikke prissatt";
  return `kr ${Math.abs(n).toLocaleString("nb-NO")}${n < 0 ? " (fradrag)" : ""}`;
}

export function relativeDay(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.round((d.setHours(0, 0, 0, 0) - now.setHours(0, 0, 0, 0)) / 86400000);
  if (diff === 0) return "i dag";
  if (diff === 1) return "i morgen";
  if (diff === -1) return "i går";
  if (diff < 0) return `${-diff} dager siden`;
  return `om ${diff} dager`;
}

export function addressLine(a) {
  if (!a) return "–";
  return [a.street, [a.postal_code, a.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
}

export function groupFields(fields = []) {
  const groups = {};
  for (const f of fields) {
    const g = f.group || "Kundevalg";
    (groups[g] ||= []).push(f);
  }
  return Object.entries(groups);
}

export function diffFields(oldFields = [], newFields = []) {
  const old = Object.fromEntries(oldFields.map((f) => [f.key, f]));
  const nw = Object.fromEntries(newFields.map((f) => [f.key, f]));
  const out = [];
  for (const [k, f] of Object.entries(nw)) {
    if (!old[k]) out.push({ key: k, label: f.label, from: null, to: f.value, kind: "lagt_til" });
    else if (old[k].value !== f.value) out.push({ key: k, label: f.label, from: old[k].value, to: f.value, kind: "endret" });
  }
  for (const [k, f] of Object.entries(old)) {
    if (!nw[k]) out.push({ key: k, label: f.label, from: f.value, to: null, kind: "fjernet" });
  }
  return out;
}
